import type {
  CashFlowReport,
  CostCenterRecord,
  DreReport,
  FinancialAccountRecord,
  FinancialAccountType,
  FinancialCategoryRecord,
  FinancialFlow,
  FinancialSummary,
  FinancialTitleDetails,
  FinancialTitleSummary,
  FinancialTransactionRecord,
  Paginated,
  PaymentMethod,
  TitleKind,
  TitleStatus,
  TransactionType,
} from '@ldembalagens/types';
import { api, apiFetch, downloadFile, type QueryParams } from '@/lib/api-client';
import { multipart } from '@/features/invoices/invoices-api';

export interface AccountInput {
  name: string;
  type?: FinancialAccountType;
  bankName?: string | null;
  agency?: string | null;
  accountNumber?: string | null;
  openingBalance?: string;
  openingDate?: string;
  isDefault?: boolean;
  status?: 'ACTIVE' | 'INACTIVE';
  notes?: string | null;
}
export interface TitleInput {
  kind: TitleKind;
  description: string;
  categoryId: string;
  costCenterId?: string | null;
  customerId?: string | null;
  supplierId?: string | null;
  amount: string;
  competenceDate: string;
  dueDate: string;
  installments?: number;
  intervalDays?: number;
  /** Parcelamento antigo: 1ª parcela controlada (as anteriores ficam pagas fora do sistema) */
  openingInstallment?: number;
  documentRef?: string | null;
  notes?: string | null;
}
export interface SettleInput {
  accountId: string;
  settledAt: string;
  amount?: string;
  interest?: string;
  fine?: string;
  discount?: string;
  method?: PaymentMethod;
  documentRef?: string | null;
  notes?: string | null;
}
export interface GenerateInput {
  categoryId: string;
  costCenterId?: string | null;
  dueDate: string;
  installments?: number;
  intervalDays?: number;
  amount?: string;
  documentRef?: string | null;
}
export interface TransactionInput {
  type: 'INCOME' | 'EXPENSE';
  accountId: string;
  amount: string;
  occurredAt: string;
  description: string;
  categoryId: string;
  costCenterId?: string | null;
  documentRef?: string | null;
  notes?: string | null;
}

export interface TitleQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TitleStatus;
  overdue?: boolean;
  open?: boolean;
  categoryId?: string;
  groupId?: string;
  from?: string;
  to?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}
export interface TransactionQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  accountId?: string;
  type?: TransactionType;
  flow?: 'INCOME' | 'EXPENSE';
  categoryId?: string;
  reconciled?: boolean;
  from?: string;
  to?: string;
}

export const financialApi = {
  summary: () => api.get<FinancialSummary>('/financial/summary'),
  cashFlow: (q: {
    granularity?: 'day' | 'month';
    from?: string;
    to?: string;
    accountId?: string;
  }) => api.get<CashFlowReport>('/financial/cash-flow', q),
  dre: (q: { from?: string; to?: string }) => api.get<DreReport>('/financial/dre', q),
  accounts: (includeInactive = false) =>
    api.get<FinancialAccountRecord[]>('/financial/accounts', { includeInactive }),
  createAccount: (i: AccountInput) => api.post<FinancialAccountRecord>('/financial/accounts', i),
  updateAccount: (id: string, i: Partial<AccountInput>) =>
    api.patch<FinancialAccountRecord>(`/financial/accounts/${id}`, i),
  deleteAccount: (id: string) => api.delete(`/financial/accounts/${id}`),
  categories: () => api.get<FinancialCategoryRecord[]>('/financial/categories'),
  createCategory: (i: { name: string; flow: FinancialFlow }) =>
    api.post<FinancialCategoryRecord>('/financial/categories', i),
  updateCategory: (id: string, i: { name?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
    api.patch<FinancialCategoryRecord>(`/financial/categories/${id}`, i),
  deleteCategory: (id: string) => api.delete(`/financial/categories/${id}`),
  costCenters: () => api.get<CostCenterRecord[]>('/financial/cost-centers'),
  createCostCenter: (i: { code: string; name: string }) =>
    api.post<CostCenterRecord>('/financial/cost-centers', i),
  updateCostCenter: (id: string, i: { name?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
    api.patch<CostCenterRecord>(`/financial/cost-centers/${id}`, i),
  deleteCostCenter: (id: string) => api.delete(`/financial/cost-centers/${id}`),
  titles: (kind: TitleKind, q: TitleQuery) =>
    api.get<Paginated<FinancialTitleSummary>>(
      kind === 'PAYABLE' ? '/financial/payables' : '/financial/receivables',
      q,
    ),
  title: (id: string) => api.get<FinancialTitleDetails>(`/financial/titles/${id}`),
  createTitle: (i: TitleInput) => api.post<FinancialTitleDetails[]>('/financial/titles', i),
  updateTitle: (id: string, i: Partial<TitleInput>) =>
    api.patch<FinancialTitleDetails>(`/financial/titles/${id}`, i),
  /** Com `receipt`, envia em multipart e o comprovante é anexado à liquidação. */
  settle: (id: string, i: SettleInput, receipt?: File | null) =>
    receipt
      ? apiFetch<FinancialTitleDetails>(`/financial/titles/${id}/settle`, {
          method: 'POST',
          body: multipart(
            receipt,
            Object.fromEntries(
              Object.entries(i)
                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => [k, String(v)]),
            ),
          ),
        })
      : api.post<FinancialTitleDetails>(`/financial/titles/${id}/settle`, i),
  attachBoleto: (id: string, file: File) =>
    apiFetch<FinancialTitleDetails>(`/financial/titles/${id}/boleto`, {
      method: 'POST',
      body: multipart(file),
    }),
  removeBoleto: (id: string) => api.delete<FinancialTitleDetails>(`/financial/titles/${id}/boleto`),
  downloadBoleto: (id: string) => downloadFile(`/financial/titles/${id}/boleto`),
  attachReceipt: (id: string, settlementId: string, file: File) =>
    apiFetch<FinancialTitleDetails>(`/financial/titles/${id}/settlements/${settlementId}/receipt`, {
      method: 'POST',
      body: multipart(file),
    }),
  downloadReceipt: (id: string, settlementId: string) =>
    downloadFile(`/financial/titles/${id}/settlements/${settlementId}/receipt`),
  setOpeningInstallment: (id: string, installment: number) =>
    api.post<FinancialTitleDetails[]>(`/financial/titles/${id}/opening-installment`, {
      installment,
    }),
  reverseSettlement: (id: string, settlementId: string, reason: string) =>
    api.post<FinancialTitleDetails>(`/financial/titles/${id}/settlements/${settlementId}/reverse`, {
      reason,
    }),
  cancelTitle: (id: string, reason: string) =>
    api.post<FinancialTitleDetails>(`/financial/titles/${id}/cancel`, { reason }),
  fromSalesOrder: (orderId: string, i: GenerateInput) =>
    api.post<FinancialTitleDetails[]>(`/financial/titles/from-sales-order/${orderId}`, i),
  fromPurchaseOrder: (orderId: string, i: GenerateInput) =>
    api.post<FinancialTitleDetails[]>(`/financial/titles/from-purchase-order/${orderId}`, i),
  transactions: (q: TransactionQuery) =>
    api.get<Paginated<FinancialTransactionRecord>>('/financial/transactions', q),
  createTransaction: (i: TransactionInput) =>
    api.post<FinancialTransactionRecord>('/financial/transactions', i),
  reverseTransaction: (id: string, reason: string) =>
    api.post<FinancialTransactionRecord>(`/financial/transactions/${id}/reverse`, { reason }),
  transfer: (i: {
    fromAccountId: string;
    toAccountId: string;
    amount: string;
    occurredAt: string;
    description?: string | null;
  }) => api.post<{ id: string }>('/financial/transfers', i),
  reconcile: (ids: string[], reconciled: boolean) =>
    api.post<{ updated: number }>('/financial/transactions/reconcile', { ids, reconciled }),
};

export const financialKeys = {
  all: ['financial'] as const,
  summary: ['financial', 'summary'] as const,
  cashFlow: (q: object) => ['financial', 'cash-flow', q] as const,
  dre: (q: object) => ['financial', 'dre', q] as const,
  accounts: (inactive: boolean) => ['financial', 'accounts', inactive] as const,
  categories: ['financial', 'categories'] as const,
  costCenters: ['financial', 'cost-centers'] as const,
  titles: (kind: TitleKind, q: TitleQuery) => ['financial', 'titles', kind, q] as const,
  title: (id: string) => ['financial', 'title', id] as const,
  transactions: (q: TransactionQuery) => ['financial', 'transactions', q] as const,
};
